import {
  Progress,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@nextui-org/react';
import type {
  CaseCargo as CargoRow,
  ShipmentCase,
} from 'common/src/api/shipment-cases';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { caseCargoSummary } from './model';

export default function CaseCargo({
  record,
  currentCargo,
}: {
  record: ShipmentCase;
  currentCargo: CargoRow[];
}) {
  const { t, currentLanguage } = useTranslation('shipmentCases');
  const summary = caseCargoSummary(record.items, currentCargo);
  const number = (value: number) =>
    new Intl.NumberFormat(currentLanguage === 'en' ? 'en-US' : 'es-CL', {
      maximumFractionDigits: 2,
    }).format(value);
  const varieties = new Set(record.items.map((item) => item.snapshot.variety))
    .size;
  return (
    <section
      className='flex flex-col gap-4'
      aria-label={t('Carga afectada registrada')}
    >
      <div className='grid grid-cols-2 gap-4 sm:grid-cols-3'>
        <div>
          <p className='text-xs text-default-400'>
            {t(summary.pallets ? 'Pallets afectados' : 'Líneas afectadas')}
          </p>
          <p className='mt-1 font-mono text-lg font-semibold'>
            {summary.pallets || record.items.length}
            {summary.comparable && (
              <span className='ml-2 font-sans text-xs font-normal text-default-400'>
                {t('de')}{' '}
                {summary.pallets ? summary.currentPallets : currentCargo.length}
              </span>
            )}
          </p>
          {summary.pallets > 0 && summary.withoutPallet > 0 && (
            <p className='text-xs text-default-400'>
              + {summary.withoutPallet} {t('líneas sin pallet')}
            </p>
          )}
        </div>
        <div>
          <p className='text-xs text-default-400'>{t('Kg afectados')}</p>
          <p className='mt-1 font-mono text-lg font-semibold'>
            {number(summary.kilos)}
            {summary.comparable && (
              <span className='ml-2 font-sans text-xs font-normal text-default-400'>
                {t('de')} {number(summary.currentKilos)}
              </span>
            )}
          </p>
        </div>
        <div>
          <p className='text-xs text-default-400'>{t('Del despacho actual')}</p>
          <p className='mt-1 font-mono text-lg font-semibold'>
            {summary.percent === null ? '—' : `${number(summary.percent)} %`}
          </p>
        </div>
      </div>
      <div className='min-w-0 overflow-x-auto rounded-medium border border-default-200'>
        <Table
          aria-label={t('Detalle de carga afectada')}
          removeWrapper
          isCompact
          classNames={{
            th: 'bg-content2/40 text-[11px] uppercase tracking-wide',
            td: 'text-[13px] py-3 border-b border-default-100',
          }}
        >
          <TableHeader>
            <TableColumn>{t('Pallet / Línea')}</TableColumn>
            <TableColumn>{t('Variedad')}</TableColumn>
            <TableColumn>{t('Calibre')}</TableColumn>
            <TableColumn className='text-right'>{t('Kg')}</TableColumn>
            <TableColumn>{t('Participación')}</TableColumn>
          </TableHeader>
          <TableBody
            items={record.items}
            emptyContent={t('Sin carga registrada.')}
          >
            {(item) => (
              <TableRow key={item.source_box_id}>
                <TableCell className='whitespace-nowrap font-mono font-medium'>
                  {item.snapshot.pallet ||
                    `${t('Sin pallet')} · #${item.source_box_id}`}
                </TableCell>
                <TableCell>{item.snapshot.variety || '—'}</TableCell>
                <TableCell className='text-default-400'>
                  {item.snapshot.caliber || '—'}
                </TableCell>
                <TableCell className='text-right font-mono'>
                  {number(Number(item.kilos))}
                </TableCell>
                <TableCell>
                  <div className='flex min-w-24 items-center gap-2'>
                    <Progress
                      aria-label={`${t('Participación')}: ${item.snapshot.pallet || item.source_box_id}`}
                      size='sm'
                      color='warning'
                      value={
                        summary.kilos > 0
                          ? (Number(item.kilos) / summary.kilos) * 100
                          : 0
                      }
                      className='min-w-12 flex-1'
                    />
                    <span className='w-16 shrink-0 whitespace-nowrap text-right font-mono text-xs text-default-400'>
                      {number(
                        summary.kilos > 0
                          ? (Number(item.kilos) / summary.kilos) * 100
                          : 0
                      )}{' '}
                      %
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <div className='flex items-center justify-between gap-3 bg-content2/20 px-4 py-3 text-sm'>
          <p className='font-semibold'>
            {t('Total')}{' '}
            <span className='ml-2 text-xs font-normal text-default-400'>
              {varieties} {t(varieties === 1 ? 'variedad' : 'variedades')}
            </span>
          </p>
          <p className='font-mono font-semibold'>
            {number(summary.kilos)} kg{' '}
            <span className='ml-3 text-xs font-normal text-default-400'>
              {summary.kilos > 0 ? '100 %' : '—'}
            </span>
          </p>
        </div>
      </div>
      <p className='text-xs leading-relaxed text-default-400'>
        {t(
          summary.comparable
            ? 'La selección registrada se compara con el packing list actual del despacho.'
            : 'Se conserva la selección registrada al crear el caso. La carga actual del despacho puede haber cambiado.'
        )}
      </p>
    </section>
  );
}
